import { FC, useEffect, useState } from "react";
import { Col, Form, FormGroup, Label, Row } from "reactstrap";
import ModalComponent from "../../shared/base-components/Modal";
import TextField from "../../shared/base-components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../../shared/base-components/Button";
import DatePicker from "../../shared/base-components/DatePicker";
import { MODAL_TYPES, RESPONSE_CODE } from "../../shared/models/Types";
import { successNotify } from "../../shared/utils/notifications";
import {
  TodoModalProps,
  TodoUpdateRequest,
  assignFormatter,
  assignPersonOptions,
  assignType,
  todoAddSchema,
  todoUpdateSchema,
} from "./Todo.constants";
import { useTodoAddMutation, useTodoUpdateMutation } from "./Todo.actions";
import SelectBox from "../../shared/base-components/SelectBox";
import SearchableSelectBox from "../../shared/base-components/SearchableSelectBox";
import { useStaffListMutation } from "../Staff/Staff.actions";
import { useRecoilValue } from "recoil";
import { isLoggedInSelector } from "../../shared/actions/Authentication.actions";

const TodoModal: FC<TodoModalProps> = ({
  userData,
  isModalOpen,
  modalType,
  toggle,
  mutate,
}) => {
  const isEdit = modalType === MODAL_TYPES.UPDATE;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(isEdit ? todoUpdateSchema : todoAddSchema),
  });

  const {
    data: todoAddData,
    mutate: todoAddMutation,
    isLoading,
  } = useTodoAddMutation();

  const { data: staffListData, mutate: staffListMutate } =
    useStaffListMutation();

  const {
    data: updateData,
    mutate: todoUpdate,
    isLoading: updateLoading,
  } = useTodoUpdateMutation();

  const { profile } = useRecoilValue(isLoggedInSelector);
  const [selectedAssign, setSelectedAssign] = useState("");

  const onSubmit: SubmitHandler<any> = (requestValues: TodoUpdateRequest) => {
    const filteredStaff = staffListData?.find(
      (staff) => staff.fullname === requestValues.assingPersonName
    )?.id;

    const selectedStaff =
      selectedAssign === assignType.ASSING_OWN ? profile?.id : filteredStaff;

    const requestForm: TodoUpdateRequest = {
      id: userData?.id,
      name: requestValues.name,
      description: requestValues.description,
      date: requestValues.date,
      person_who_assign:
        selectedAssign === assignType.ASSING_ALL ? null : selectedStaff,
    };

    isEdit ? todoUpdate(requestForm) : todoAddMutation(requestForm);
  };

  const onTodoCompleteHandler = () => {
    todoUpdate({ id: userData?.id, person_who_complete: profile?.id });
  };

  useEffect(() => {
    if (todoAddData?.status === RESPONSE_CODE.CREATED) {
      successNotify("İş başarılı bir şekilde oluşturuldu");
      toggle();
      mutate({});
    }
  }, [todoAddData]);

  useEffect(() => {
    if (updateData?.status === RESPONSE_CODE.SUCCESS_CODE) {
      successNotify("iş başarılı bir şekilde güncellendi");
      toggle();
      mutate({});
    }
  }, [updateData]);

  useEffect(() => {
    staffListMutate({});
  }, []);

  const valueData = staffListData?.map((staff) => ({ value: staff.fullname }));

  return (
    <ModalComponent
      header={isEdit ? "İş  Güncelle" : "İş Ekle"}
      description={
        isEdit
          ? "İş Güncellemek için ilgili alanı düzeltip onaylayın"
          : "İş eklemek için gerekli alanları doldurunuz."
      }
      isOpen={isModalOpen}
      toggle={toggle}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="custom-form mt-4 pt-2">
        <Row>
          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Yapılıcak İş</Label>
              <TextField
                control={control}
                name="name"
                defaultValue={isEdit ? userData?.name : ""}
                type="text"
                className="form-control"
                placeholder="Yapılacak iş adı"
                error={errors.name?.message}
              />
            </FormGroup>
          </Col>

          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label className="form-label">Son Tarih</Label>
              <DatePicker
                control={control}
                name="date"
                type="text"
                className="form-control"
                defaultValue={
                  isEdit ? userData?.date || new Date() : new Date()
                }
                error={errors.date?.message}
              />
            </FormGroup>
          </Col>

          <Col sm="12" md="12" lg="12">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Açıklama</Label>
              <TextField
                control={control}
                className="form-control"
                name="description"
                defaultValue={isEdit ? userData?.description : ""}
                type="textarea"
                error={errors.description?.message}
              />
            </FormGroup>
          </Col>

          <Col sm="12" md="6" lg="6">
            <FormGroup className="mb-3">
              <Label htmlFor="validationCustom02">Atanacak Kişi</Label>
              <SelectBox
                options={assignPersonOptions}
                defaultValue={
                  isEdit
                    ? assignFormatter(
                        userData?.person_who_assign?.id || undefined,
                        profile?.id
                      )
                    : ""
                }
                onChangeHandler={(e) => setSelectedAssign(e.target.value)}
                name="assingPerson"
                className="form-select"
              />
            </FormGroup>
          </Col>

          {selectedAssign === assignType.ASSING_PERSON && (
            <Col sm="12" md="6" lg="6">
              <FormGroup className="mb-3">
                <Label htmlFor="validationCustom02">Atanacak Kişi</Label>
                {valueData && (
                  <SearchableSelectBox
                    control={control}
                    options={valueData}
                    name="assingPersonName"
                    className="form-control"
                  />
                )}
              </FormGroup>
            </Col>
          )}

          <Col md="12">
            <FormGroup className="mb-3">
              <Button
                loading={isEdit ? updateLoading : isLoading}
                text={isEdit ? "İş Güncelle" : "İş Ekle"}
                variant="primary"
              />
            </FormGroup>

            {isEdit && (
              <FormGroup className="mb-3">
                <Button
                  type="button"
                  onClick={onTodoCompleteHandler}
                  loading={isEdit ? updateLoading : isLoading}
                  text="Tamamla"
                  variant="success"
                  iconSrc="check-double"
                />
              </FormGroup>
            )}
          </Col>
        </Row>
      </Form>
    </ModalComponent>
  );
};

export default TodoModal;
